import { memo } from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '@components/badge';
import { Card } from '@components/card';
import { IconFancyContainer } from '@components/icon-fancy-container';
import { Shapes } from '@components/shapes';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import type { Color } from '@styles/theme';

import { JobLabels } from 'components/job-labels';
import { useStore } from 'models/Provider';
import type { JobListRow } from 'types/jobs';

import {
  jobCounts,
  newApplicantsValueColor,
  jobDateText,
  jobDetailUrl,
  jobMetaLine,
  jobTitle,
  Translate,
} from './job-display';
import { useJobStateIconContainer } from './job-state';
import { jobsLocale } from './jobs-config';

type MobileJobListItemProps = {
  job: JobListRow;
};

export const MobileJobListItem = memo(({ job }: MobileJobListItemProps) => {
  const { t, lang, current } = useStore();
  const translate = t as Translate;
  const { t: tJobs } = useScopedTranslate(jobsLocale);
  const counts = jobCounts(job);
  const stateIconContainer = useJobStateIconContainer(job);
  const labels = job.labels ?? [];
  const defaultLanguage = current?.info?.defaultLanguage;
  const title = jobTitle({ job, defaultLanguage, t: translate });

  const privateBadge =
    job.visibility === 'private' ? (
      <Badge variant="purple" text={tJobs('privateJob')} />
    ) : undefined;

  return (
    <Link to={jobDetailUrl(job)}>
      <Card interactive spaceX={6} spaceY={4} dataCy={title}>
        <Stack space={2}>
          <Stack space={2} orientation="horizontal" align="center">
            <IconFancyContainer size="md" {...stateIconContainer} />
            {/*
              `ellipsisWithTooltip` is also what lets the title shrink: a flex item only has an automatic minimum
              size while its overflow is visible, and overflow-wrap doesn't reduce it.
            */}
            <Text font="bold16" text={title} ellipsisWithTooltip />
          </Stack>
          <Stack>
            <Text
              font="regular14"
              color="textSecondary"
              text={jobMetaLine({ job, lang, defaultLanguage, t: translate })}
            />
            <Text
              font="regular14"
              color="textSecondary"
              text={jobDateText({ job, lang, t: translate })}
            />
          </Stack>
          <Stack space={4} orientation="horizontal" align="center">
            <CountPair value={counts.total} label={tJobs('count_applicants')} />
            {/* An archived job cannot receive new applicants. */}
            {!job.isArchived && (
              <CountPair
                value={counts.new}
                label={tJobs('count_new')}
                valueColor={newApplicantsValueColor(counts.new)}
              />
            )}
          </Stack>
          {/*
            Uncapped: the desktop "+N more" tooltip triggers on pointerenter/focus, and the
            whole card is a Link — on touch a tap navigates rather than revealing the rest.
            No guard needed: JobLabels renders null when it has nothing, and Stack's `gap`
            costs nothing for a null child.
          */}
          <JobLabels labels={labels} leading={privateBadge} />
        </Stack>
      </Card>
    </Link>
  );
});

type CountPairProps = {
  value: number;
  label: string;
  valueColor?: Color;
};

const CountPair = ({ value, label, valueColor }: CountPairProps) => (
  <Stack space={2} orientation="horizontal" align="center">
    <Text font="bold14" color={valueColor} text={value} />
    <Shapes.Circle size={4} color="elementPurple50" />
    <Text font="bold14" text={label} />
  </Stack>
);
